import React, { useState, useMemo, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'
import _ from 'lodash'

import Documents, { DocumentsHandle, DocumentsProps } from '@/pages/diagnostic/components/Documents'
import { GenerateFSPFormValues } from '@/pages/diagnostic/components/GenerateFSPDialog'
import { GenerateReportFormValues } from '@/pages/diagnostic/components/GenerateReportDialog'

import { documentOfflineStore, OfflineDocument, OfflineConsultation } from '@/core/offline'

type OfflineDocumentsProps = {
  consultation: OfflineConsultation
  onConsultationUpdate: (consultation: OfflineConsultation) => void
}

export function OfflineDocuments(props: OfflineDocumentsProps) {
  const { consultation, onConsultationUpdate } = props
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()

  const documentsRef = useRef<DocumentsHandle>(null)

  const [isUploading, setIsUploading] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const [isGeneratingReport, setIsGeneratingReport] = useState(false)
  const [isGeneratingFSP, setIsGeneratingFSP] = useState(false)
  const [isSendingByEmail, setIsSendingByEmail] = useState(false)
  const [documents, setDocuments] = useState<DocumentsProps['documents']>([])

  // Load documents for this consultation
  React.useEffect(() => {
    const loadDocuments = async () => {
      try {
        const consultationDocs = await documentOfflineStore.getByObjectId('consultation', consultation._id)
        const documentsData = await Promise.all(
          consultationDocs.map(async (doc: OfflineDocument) => {
            try {
              // Try to create URL from stored file data if available
              let src: string = ''
              if (doc._fileData && doc._fileType) {
                const blob = await documentOfflineStore.getFileBlob(doc)
                if (blob) {
                  src = URL.createObjectURL(blob)
                }
              }

              // Extract file extension from filename
              const nameParts = doc.filename.split('.')
              const extension = nameParts.length > 1 ? nameParts.pop() || '' : ''
              const name = nameParts.join('.')

              return {
                key: doc.key,
                name: name,
                type: extension,
                src: src,
              }
            } catch (error) {
              console.error('Error creating URL for document:', error)
              const nameParts = doc.filename.split('.')
              const extension = nameParts.length > 1 ? nameParts.pop() || '' : ''
              const name = nameParts.join('.')

              return {
                key: doc.key,
                name: name,
                type: extension,
                src: '',
              }
            }
          })
        )
        setDocuments(documentsData)
      } catch (error) {
        console.error('Error loading documents:', error)
      }
    }

    loadDocuments()
  }, [consultation._id])

  const handleUpload = useCallback(
    async (file: File) => {
      try {
        setIsUploading(true)

        // Convert file to base64
        const base64Data = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader()
          reader.readAsDataURL(file)
          reader.onload = () => {
            const result = reader.result as string
            // Remove the data URL prefix to get just the base64 data
            resolve(result.split(',')[1])
          }
          reader.onerror = (error) => reject(error)
        })

        // Create offline document with all required properties
        const documentData = {
          filename: file.name,
          size: file.size,
          mimeType: file.type,
          base64Data: base64Data,
          consultationId: consultation._id,
        }

        const savedDocument = await documentOfflineStore.createOfflineDocument(documentData)

        // Create URL for preview
        const blob = new Blob([await file.arrayBuffer()], { type: file.type })
        const url = URL.createObjectURL(blob)

        const nameParts = file.name.split('.')
        const extension = nameParts.length > 1 ? nameParts.pop() || '' : ''
        const name = nameParts.join('.')

        const newDocument: DocumentsProps['documents'][0] = {
          key: savedDocument.key,
          name: name,
          type: extension,
          src: url,
        }

        setDocuments((prev) => [...prev, newDocument])
        enqueueSnackbar(t('notification.document.create.success'), { variant: 'success' })
      } catch (error) {
        console.error('Error uploading file:', error)
        enqueueSnackbar(t('notification.document.create.error'), { variant: 'error' })
      } finally {
        setIsUploading(false)
      }
    },
    [consultation._id, enqueueSnackbar, t]
  )

  const handleDelete = useCallback(
    async (deletedDocuments: DocumentsProps['documents']) => {
      try {
        setIsDeleting(true)

        for (const doc of deletedDocuments) {
          await documentOfflineStore.delete(doc.key)
          // Revoke URL to free memory
          if (doc.src) {
            URL.revokeObjectURL(doc.src)
          }
        }

        setDocuments((prev) => prev.filter((d) => !deletedDocuments.some((dd) => dd.key === d.key)))
        enqueueSnackbar(t('notification.document.delete.success'), { variant: 'success' })
      } catch (error) {
        console.error('Error deleting documents:', error)
        enqueueSnackbar(t('notification.document.delete.error'), { variant: 'error' })
      } finally {
        setIsDeleting(false)
      }
    },
    [enqueueSnackbar, t]
  )

  const handleExport = useCallback(
    async (exportedDocuments: DocumentsProps['documents']) => {
      try {
        setIsExporting(true)
        // In offline mode, we can't export to server, but we could download as zip
        enqueueSnackbar('Export not available in offline mode', { variant: 'info' })
      } catch (error) {
        console.error('Error exporting documents:', error)
        enqueueSnackbar('Error exporting documents', { variant: 'error' })
      } finally {
        setIsExporting(false)
      }
    },
    [enqueueSnackbar]
  )

  const handleGenerateReport = useCallback(
    async (values: GenerateReportFormValues) => {
      try {
        setIsGeneratingReport(true)
        // In offline mode, we can't generate reports to server
        enqueueSnackbar('Report generation not available in offline mode', { variant: 'info' })
      } catch (error) {
        console.error('Error generating report:', error)
        enqueueSnackbar('Error generating report', { variant: 'error' })
      } finally {
        setIsGeneratingReport(false)
      }
    },
    [enqueueSnackbar]
  )

  const handleGenerateFSP = useCallback(
    async (values: GenerateFSPFormValues) => {
      try {
        setIsGeneratingFSP(true)
        // In offline mode, we can't generate FSP to server
        enqueueSnackbar('FSP generation not available in offline mode', { variant: 'info' })
      } catch (error) {
        console.error('Error generating FSP:', error)
        enqueueSnackbar('Error generating FSP', { variant: 'error' })
      } finally {
        setIsGeneratingFSP(false)
      }
    },
    [enqueueSnackbar]
  )

  const handlePrintImages = useCallback(
    async (printedDocuments: DocumentsProps['documents']) => {
      try {
        // In offline mode, we can still print images
        const imageDocuments = printedDocuments.filter((doc) => ['jpg', 'jpeg', 'png', 'gif'].includes(doc.type.toLowerCase()))

        if (imageDocuments.length === 0) {
          enqueueSnackbar('No images to print', { variant: 'info' })
          return
        }

        // Create a simple print window with images
        const printWindow = window.open('', '_blank')
        if (!printWindow) return

        printWindow.document.write(`
                <html>
                    <head>
                        <title>Print Images</title>
                        <style>
                            body { margin: 20px; }
                            img { max-width: 100%; margin: 10px 0; page-break-after: always; }
                        </style>
                    </head>
                    <body>
                        ${imageDocuments.map((doc) => `<img src="${doc.src}" alt="${doc.name}" />`).join('')}
                    </body>
                </html>
            `)
        printWindow.document.close()
        printWindow.print()
      } catch (error) {
        console.error('Error printing images:', error)
        enqueueSnackbar('Error printing images', { variant: 'error' })
      }
    },
    [enqueueSnackbar]
  )

  const handleSendDocumentsByEmail = useCallback(
    async (emails: string[], documents: DocumentsProps['documents']) => {
      try {
        setIsSendingByEmail(true)
        // In offline mode, we can't send emails
        enqueueSnackbar('Email sending not available in offline mode', { variant: 'info' })
      } catch (error) {
        console.error('Error sending documents by email:', error)
        enqueueSnackbar('Error sending documents by email', { variant: 'error' })
      } finally {
        setIsSendingByEmail(false)
      }
    },
    [enqueueSnackbar]
  )

  const handleRefresh = useCallback(() => {
    // Reload documents from IndexedDB
    const loadDocuments = async () => {
      try {
        const consultationDocs = await documentOfflineStore.getByObjectId('consultation', consultation._id)
        const documentsData = await Promise.all(
          consultationDocs.map(async (doc: OfflineDocument) => {
            let src: string = ''
            if (doc._fileData && doc._fileType) {
              const blob = await documentOfflineStore.getFileBlob(doc)
              if (blob) {
                src = URL.createObjectURL(blob)
              }
            }

            const nameParts = doc.filename.split('.')
            const extension = nameParts.length > 1 ? nameParts.pop() || '' : ''
            const name = nameParts.join('.')

            return {
              key: doc.key,
              name: name,
              type: extension,
              src: src,
            }
          })
        )
        setDocuments(documentsData)
        enqueueSnackbar('Documents refreshed', { variant: 'success' })
      } catch (error) {
        console.error('Error refreshing documents:', error)
        enqueueSnackbar('Error refreshing documents', { variant: 'error' })
      }
    }
    loadDocuments()
  }, [consultation._id, enqueueSnackbar])

  // Static data for offline mode
  const emails = useMemo(() => {
    return [consultation.patient?.email].filter(Boolean) as string[]
  }, [consultation])

  const initialReportValues: GenerateReportFormValues = useMemo(
    () => ({
      establishment: {
        name: '',
        address: '',
        phone: '',
      },
      logo: '',
      title: t('page.liveConsultation.generateReportDialog.pdf.title'),
      startDate: consultation?.startDate || '',
      pds: '',
      patient: {
        firstName: consultation?.patient?.userInfo?.name || '',
        lastName: consultation?.patient?.userInfo?.surname || '',
        gender: consultation?.patient?.userInfo?.gender || '',
        id: consultation?.patient?.id || '',
        birthday: consultation?.patient?.userInfo?.birthday || '',
        secu: consultation?.patient?.secu || '',
        attendingPhysician: '',
      },
      mesures: [],
      mesuresNote: '',
      images: [],
      audios: [],
      pdfs: [],
      biologicals: [],
      stethoscopes: [],
      documents: documents.map((doc) => ({
        checked: false,
        filename: doc.name,
        key: doc.key,
        url: doc.src,
      })), // Add the documents property with correct structure
      signatureMode: 'manual' as const,
      eSignature: '',
      manualSignature: '',
    }),
    [consultation, t]
  )

  const initialFSPValues: GenerateFSPFormValues = useMemo(
    () => ({
      Acts: [],
    }),
    []
  )

  return (
    <Documents
      ref={documentsRef}
      documents={documents}
      dictaphoneSentences={[]}
      conclusionsSentences={[]}
      disableEPrescription={true}
      emails={emails}
      isDeleting={isDeleting}
      isDoctor={false}
      isExporting={isExporting}
      isSendingByEmail={isSendingByEmail}
      isGeneratingReport={isGeneratingReport}
      isGeneratingFSP={isGeneratingFSP}
      isLocal={true}
      initialReportValues={initialReportValues}
      initialFSPValues={initialFSPValues}
      onDelete={handleDelete}
      onExport={handleExport}
      onGenerateReport={handleGenerateReport}
      onGenerateFsp={handleGenerateFSP}
      onGenereatePrescriptionButtonClick={() => {
        enqueueSnackbar('Prescription generation not available in offline mode', { variant: 'info' })
      }}
      onPrintImages={handlePrintImages}
      onQrClick={() => {
        enqueueSnackbar('QR code not available in offline mode', { variant: 'info' })
      }}
      onSaveFileAnnotation={() => {
        enqueueSnackbar('File annotation not available in offline mode', { variant: 'info' })
      }}
      onSaveFileName={() => {
        enqueueSnackbar('File renaming not available in offline mode', { variant: 'info' })
      }}
      onSendDocumentsByEmail={handleSendDocumentsByEmail}
      onUpload={handleUpload}
      onRefresh={handleRefresh}
      qrCode=""
      readOnly={false}
    />
  )
}
